import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { HoldRatioBadgeProps } from '../types';
import { isUnhealthyRatio } from '../utils';

/**
 * Styled Chip for Hold Ratio Badge
 */
interface StyledChipProps {
  isUnhealthy: boolean;
}

const StyledBadgeChip = styled(Chip)<StyledChipProps>(
  ({ theme, isUnhealthy }) => ({
    height: '24px',
    padding: theme.spacing(0, 1),
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.07px',
    border: `1px solid ${isUnhealthy ? theme.palette.error.main : theme.palette.success.main}`,
    backgroundColor: isUnhealthy
      ? 'rgba(254, 75, 75, 0.1)'
      : 'rgba(13, 231, 126, 0.1)',
    color: isUnhealthy ? theme.palette.error.main : theme.palette.success.main,
    borderRadius: '6px',

    '& .MuiChip-label': {
      padding: 0,
    },
  }),
);

/**
 * Hold Ratio Badge Component
 * Displays the hold ratio with color coding:
 * - Red: Unhealthy (losers held longer than winners, ratio > 1)
 * - Green: Healthy (winners held longer than losers, ratio < 1)
 */
export const HoldRatioBadge: FC<HoldRatioBadgeProps> = ({ ratio }) => {
  const { t } = useTranslation('communityStats');
  const isUnhealthy = isUnhealthyRatio(ratio);
  const formattedRatio = ratio < 0.1 ? ratio.toFixed(2) : ratio.toFixed(1);
  const label = t('winLossDuration.holdRatio', { value: formattedRatio });

  return <StyledBadgeChip label={label} isUnhealthy={isUnhealthy} />;
};
